import React from 'react';
import classNames from 'classnames';
import {Field} from 'redux-form';
import {Form, Row, Col} from 'react-bootstrap';
import Button from '@material-ui/core/Button';
import {Class as PageIcon, Comment as CommentIcon, Edit as EditIcon, Cancel as CancelIcon} from '@material-ui/icons';
import {isEmpty} from 'lodash/lang';
import {AutoSuggestSelect} from 'components/common';
import * as fields from 'components/common/fields';
import {fetchBooks} from 'api';
import {formatErrors} from 'actions/errors';
import * as validators from 'utils/validators';
import {makeForm, momentDatetime, getReadingReactions} from 'utils/common';
import * as permissions from 'utils/permissions';

const getAvailableDates = (firstDate, numberOfDates) => {
  if (numberOfDates === null) return [];
  const dates = new Array(numberOfDates).fill(0).map((_, idx) => {
    const date =
      idx === 0
        ? firstDate
        : firstDate
            .clone()
            .subtract(idx, 'day')
            .startOf('day');
    const formattedDate = date.format('dddd');
    return {
      value: date.toJSON(),
      date: (
        <Button variant="contained" fullWidth>
          {idx === 0 ? 'Today' : idx === 1 ? `Yesterday (${formattedDate})` : formattedDate}
        </Button>
      ),
    };
  });
  return dates.reverse();
};

class ReadingForm extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      dateSelectHidden: false,
      dateSelected: props.firstDate.toJSON(),
    };
    this.showDateSelect = this.showDateSelect.bind(this);
    this.dateSelectHidden = this.dateSelectHidden.bind(this);
    this.handleDateChange = this.handleDateChange.bind(this);
    this.fetchBooks = this.fetchBooks.bind(this);
    this.promptTextCreator = this.promptTextCreator.bind(this);
  }

  showDateSelect() {
    this.setState({
      dateSelectHidden: false,
    });
  }

  dateSelectHidden() {
    this.setState({
      dateSelectHidden: true,
    });
  }

  handleDateChange(dateSelected) {
    this.setState({
      dateSelected,
      dateSelectHidden: true,
    });
  }

  fetchBooks(value) {
    const {formatErrors} = this.props;
    return fetchBooks(value)
      .then(({records}) =>
        records.map((record) => ({
          id: record.id,
          name: record.title,
        })),
      )
      .catch((err) => formatErrors(err));
  }

  promptTextCreator(label) {
    return `Create Book: ${label}`;
  }

  render() {
    const {dateSelected, dateSelectHidden} = this.state;
    const {
      studentSetReadingPeriod,
      reactionOptions,
      readonly,
      dateCreated,
      dateOptions,
      handleSubmit,
      pristine,
      submitting,
    } = this.props;
    return (
      <Form className="reading-form" onSubmit={handleSubmit}>
        <Row>
          {readonly ? (
            <Col className="reading-date" md={12}>
              <h4 className="date-today">{dateCreated && momentDatetime(dateCreated).format('dddd Do MMMM, YYYY')}</h4>
            </Col>
          ) : studentSetReadingPeriod === null ? (
            <Col md={12}>
              <Field
                name="date"
                label="Date"
                maxDate={momentDatetime()}
                component={fields.DatePicker}
                required
                validate={[validators.isRequired]}
              />
            </Col>
          ) : (
            <Col className="reading-date" md={12}>
              <h4 className="date-today">
                {momentDatetime(dateSelected).format('dddd Do MMMM, YYYY')}
                {dateSelectHidden ? (
                  <a href={null} className="flex-row-center pull-right" onClick={this.showDateSelect}>
                    <EditIcon />
                    Edit
                  </a>
                ) : (
                  <a href={null} className="flex-row-center pull-right" onClick={this.dateSelectHidden}>
                    <CancelIcon />
                    Cancel
                  </a>
                )}
              </h4>
              <div className={classNames({hidden: dateSelectHidden})}>
                <Field
                  name="date"
                  label="Date"
                  clickableKey="date"
                  valueKey="value"
                  options={dateOptions}
                  onValueChange={this.handleDateChange}
                  component={fields.ClickSelectField}
                  required
                  validate={[validators.isRequired]}
                />
              </div>
            </Col>
          )}
          <Col md={12}>
            <AutoSuggestSelect
              name="book"
              label="Book Title"
              labelKey="name"
              valueKey="id"
              onSearch={this.fetchBooks}
              required
              validate={[validators.isRequired]}
              disabled={readonly}
              creatable
              promptTextCreator={this.promptTextCreator}
            />
          </Col>
          <Col md={12}>
            <Field
              name="reaction"
              label="Rate your Reading"
              clickableKey="image"
              valueKey="value"
              options={reactionOptions}
              component={fields.ClickSelectField}
              required
              validate={[validators.isRequired]}
              hasSelectedEffect
              hasOverlay={false}
              readOnly={readonly}
            />
          </Col>
          <Col className="read-page" md={12}>
            <span className="prefix">
              <PageIcon />
              I'm up to page
            </span>
            <Field
              name="page"
              label="#"
              className="font-cursive"
              component={fields.Input}
              validate={[validators.isPage]}
              autoComplete="off"
              disabled={readonly}
            />
          </Col>
          <Col className="read-comment" md={12}>
            <span className="prefix">
              <CommentIcon />
            </span>
            <Field name="comment" label="Leave a comment" component={fields.Textarea} disabled={readonly} />
          </Col>
        </Row>
        {!readonly && (
          <Row>
            <Col xs={12}>
              <Button type="submit" variant="contained" color="primary" fullWidth disabled={pristine || submitting}>
                Submit
              </Button>
            </Col>
          </Row>
        )}
      </Form>
    );
  }
}

const mapStateToProps = {
  Creation: (state, ownProps) => {
    const {lastReading, activeReadingReactions, studentSetReadingPeriod} = ownProps;
    const firstDate = momentDatetime().startOf('day');
    const dateOptions = getAvailableDates(firstDate, studentSetReadingPeriod);
    const initialValues = isEmpty(lastReading)
      ? {
          date: firstDate.toJSON(),
        }
      : {
          date: firstDate.toJSON(),
          book: {id: lastReading.bookId, name: lastReading.bookTitle},
        };
    return {
      reactionOptions: getReadingReactions(activeReadingReactions),
      firstDate,
      dateOptions,
      initialValues,
    };
  },
  Update: (state, ownProps) => {
    const {studentSetReadingPeriod} = ownProps;
    const {account, readings} = state;
    const currentReading = readings.get('currentReading').toJS();
    const dateCreated = !currentReading.dateCreated
      ? null
      : momentDatetime(currentReading.dateCreated)
          .startOf('day')
          .toJSON();
    const firstDate = momentDatetime().startOf('day');
    const dateOptions = getAvailableDates(firstDate, studentSetReadingPeriod);
    console.log(dateOptions);
    const isMeReader = permissions.isReader(account.getIn(['user', 'role']));
    const readonly =
      ownProps.readOnly ||
      !isMeReader ||
      (dateCreated &&
        studentSetReadingPeriod &&
        firstDate
          .clone()
          .subtract(studentSetReadingPeriod, 'day')
          .isSameOrAfter(dateCreated));
    return {
      reactionOptions: getReadingReactions(ownProps.activeReadingReactions),
      readonly,
      firstDate,
      dateOptions,
      dateCreated: dateCreated,
      initialValues: {
        ...currentReading,
        book: currentReading.bookId,
        date: dateCreated,
      },
    };
  },
};

const mapDispatchToProps = (dispatch) => ({
  formatErrors: (...args) => dispatch(formatErrors(...args)),
});

export default {
  Creation: makeForm(ReadingForm, 'ReadingForm.Creation', mapStateToProps.Creation, mapDispatchToProps),
  Update: makeForm(ReadingForm, 'ReadingForm.Update', mapStateToProps.Update, mapDispatchToProps),
};
